import {
  useMemo,
  MouseEvent,
  FunctionComponent,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react'
import debounce from 'lodash.debounce'

import useTracking from '../hooks/tracking/useTracking'
import useBlickTVStoryContext from './useBlickTVStoryContext'
import config from '@config'
import BlickTVStoryComponent, * as BlickTVStoryComponents from '@components/Video/BlickTVStory'
import translate from '@i18n'
import { ChapterAPIResponse, APIChapter, Chapter } from '../types/story'

import {
  formatDuration,
  calculateChapterTimelinePosition,
  normalizePlayerCurrentTimeValue,
  hasChaptersPlaceholder,
} from './utils'

import { getPlayerType } from '../utils'

import { Vtype } from '@components/AppNexus/types'
import { BlickTVTeaserProps } from '@widgets/BlickTVTeaser/types'
import { useQuery } from 'react-query'

const {
  chaptersApiUrl,
  story: {
    dvrWindow,
    pollingIntervalInMilliseconds,
    showGoToLiveDelayInMilliseconds,
  },
} = config.video

export type BlickTVStoryProps = BlickTVTeaserProps

const formatChapters = (chapters: APIChapter[]): Chapter[] =>
  chapters.reverse().map((chapter: APIChapter, index: number) => ({
    ...chapter,
    duration: formatDuration(chapter?.length),
    isLive: index === chapters?.length - 1, // the last chapter should be live
    image: chapter?.filename
      ? {
          src: `/chapters/${chapter?.filename}`,
          alt: chapter?.headline,
        }
      : undefined,
  }))

const getChapters = async (): Promise<Chapter[]> => {
  const res = await fetch(`${chaptersApiUrl}?dvr=${dvrWindow}`)
  const data: ChapterAPIResponse = await res.json()

  return data ? formatChapters(data) : []
}

const BlickTVStory: FunctionComponent<BlickTVStoryProps> = (props) => {
  const {
    chapters,
    setChapters,
    activeChapter,
    setActiveChapter,
    timelinePointers,
    playerRef,
    setUserLastSelectedTimestamp,
  } = useBlickTVStoryContext()

  const swiperRef = useRef<BlickTVStoryComponents.SwiperClass | null>(null)

  const [showRestartButton, setShowRestartButton] = useState<boolean>(false)
  const [showGoToLiveButton, setShowGoToLiveButton] = useState<boolean>(false)

  const showGoToLiveButtonTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const {
    title,
    video: {
      isBlickTV,
      teaserAutoplay: autoplay,
      allowFloating,
      position,
      duration,
      videoId,
    },
  } = props || {}

  const playerType = getPlayerType({ isBlickTV, duration })

  const trackingCallbacks = useTracking({
    isBlickTV,
    allowFloating,
    position,
    autoplay,
    playerRef,
    playerType,
    videoId,
    title,
    duration,
    isInVideoOnlyArticle: false,
    vtype: Vtype.LIVESTREAM,
  })

  const { data: chaptersData } = useQuery<Chapter[]>(
    ['chapters'],
    () => getChapters(),
    {
      refetchInterval: pollingIntervalInMilliseconds,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
    }
  )

  useEffect(() => {
    if (chaptersData) {
      setChapters(chaptersData)
    }
  }, [chaptersData, setChapters])

  const activeChapterIndex = useMemo(
    () =>
      activeChapter?.id && chapters
        ? chapters?.findIndex((chapter) => chapter?.id === activeChapter?.id)
        : -1,
    [activeChapter?.id, chapters]
  )

  const seekTo = useCallback(
    (seconds: any) => {
      playerRef.current?.seekTo?.(seconds)
    },
    [playerRef]
  )

  const seekToLive = useCallback(() => {
    playerRef.current?.seekToLive?.()
  }, [playerRef])

  const slideTo = useCallback((slideIndex: any) => {
    swiperRef.current?.slideTo(slideIndex)
  }, [])

  const seekToChapterPosition = useCallback(
    (chapterData?: Chapter) => {
      if (chapterData) {
        const player = playerRef?.current?.player?.()

        if (!player || !chapterData.timestamp) {
          return
        }

        const playerNewCurrentTimeValue = calculateChapterTimelinePosition(
          chapterData.timestamp,
          timelinePointers
        )

        const seekToPosition = normalizePlayerCurrentTimeValue(
          playerNewCurrentTimeValue,
          player
        )

        seekTo(seekToPosition)
      }
    },
    [playerRef, seekTo, timelinePointers]
  )

  const setLastChapterAsActive = useCallback(() => {
    if (chapters?.length && !hasChaptersPlaceholder(chapters)) {
      const lastChapter = chapters[chapters.length - 1]

      if (lastChapter) {
        setActiveChapter(lastChapter)
      }
    }
  }, [chapters, setActiveChapter])

  const makeActiveChapterVisibleWithinView = useCallback(() => {
    const hasActiveSlide = activeChapterIndex >= 0

    if (hasActiveSlide) {
      const currentActiveIndex = swiperRef.current?.activeIndex
      const isSlideVisible =
        activeChapterIndex === currentActiveIndex ||
        activeChapterIndex - 1 === currentActiveIndex ||
        activeChapterIndex + 1 === currentActiveIndex

      if (!isSlideVisible) {
        slideTo(activeChapterIndex)
      }
    }
  }, [activeChapterIndex, slideTo])

  // set last chapter as initial value for activeChapter
  useEffect(() => {
    if (!activeChapter?.id) {
      setLastChapterAsActive()
    }
  }, [activeChapter?.id, setLastChapterAsActive])

  useEffect(() => {
    if (activeChapter?.id) {
      makeActiveChapterVisibleWithinView()
    }
  }, [activeChapter?.id, makeActiveChapterVisibleWithinView])

  // show GoToLiveButton if activeChapter is not live (with delay 5 sec)
  useEffect(() => {
    if (activeChapter && !activeChapter?.isLive && !showGoToLiveButton) {
      setShowRestartButton(false)

      showGoToLiveButtonTimeoutRef.current = setTimeout(() => {
        setShowGoToLiveButton(true)
      }, showGoToLiveDelayInMilliseconds)
    }

    return () => {
      if (showGoToLiveButtonTimeoutRef.current) {
        clearTimeout(showGoToLiveButtonTimeoutRef.current)
      }
    }
  }, [activeChapter, showGoToLiveButton])

  // show RestartButton if activeChapter is live
  useEffect(() => {
    if (activeChapter && activeChapter?.isLive) {
      if (showGoToLiveButtonTimeoutRef.current) {
        clearTimeout(showGoToLiveButtonTimeoutRef.current)
      }

      setShowGoToLiveButton(false)
      setShowRestartButton(true)
    }
  }, [activeChapter])

  const handleOnClickRestart = useCallback(() => {
    makeActiveChapterVisibleWithinView()
    seekToChapterPosition(activeChapter)

    trackingCallbacks?.onChapterButtonClick({
      uniqueChapterId: activeChapter?.id,
      chapterHeadline: activeChapter?.headline,
      chapterTimecode: activeChapter?.timestamp,
      chapterLength: activeChapter?.length,
      buttonLabel: translate('blickTVStory.restartCurrentChapter'),
    })
  }, [
    activeChapter,
    makeActiveChapterVisibleWithinView,
    seekToChapterPosition,
    trackingCallbacks,
  ])

  const handleOnClickGoToLive = useCallback(() => {
    setUserLastSelectedTimestamp(Date.now().valueOf())
    setLastChapterAsActive()
    seekToLive()

    trackingCallbacks?.onChapterButtonClick({
      uniqueChapterId: activeChapter?.id,
      chapterHeadline: activeChapter?.headline,
      chapterTimecode: activeChapter?.timestamp,
      chapterLength: activeChapter?.length,
      buttonLabel: translate('blickTVStory.goToLive'),
    })
  }, [
    setUserLastSelectedTimestamp,
    activeChapter?.headline,
    activeChapter?.id,
    activeChapter?.length,
    activeChapter?.timestamp,
    seekToLive,
    setLastChapterAsActive,
    trackingCallbacks,
  ])

  const handleOnClickChapter = useCallback(
    (_e: MouseEvent<HTMLDivElement>, _id: string, chapterData: Chapter) => {
      setUserLastSelectedTimestamp(Date.now().valueOf())
      setActiveChapter(chapterData)

      seekToChapterPosition(chapterData)

      trackingCallbacks?.onChapterClick({
        uniqueChapterId: chapterData?.id,
        chapterHeadline: chapterData?.headline,
        chapterTimecode: chapterData?.timestamp,
        chapterLength: chapterData?.length,
      })
    },
    [
      seekToChapterPosition,
      setActiveChapter,
      trackingCallbacks,
      setUserLastSelectedTimestamp,
    ]
  )

  const handleOnSliderMove = useCallback(() => {
    trackingCallbacks?.onChapterSwipe({
      uniqueChapterId: activeChapter?.id,
      chapterHeadline: activeChapter?.headline,
      chapterTimecode: activeChapter?.timestamp,
      chapterLength: activeChapter?.length,
    })
  }, [
    trackingCallbacks,
    activeChapter?.id,
    activeChapter?.headline,
    activeChapter?.timestamp,
    activeChapter?.length,
  ])

  // onSliderMove fires multiple times when user swipes
  // `debounce` will force it to be fired only once during swipe action
  const handleOnSliderMoveWithDebounce = useMemo(
    () => debounce(handleOnSliderMove, 500),
    [handleOnSliderMove]
  )

  // cancel debounce on unmount
  useEffect(() => {
    return () => {
      handleOnSliderMoveWithDebounce.cancel?.()
    }
  }, [handleOnSliderMoveWithDebounce])

  const handleOnNavigationChange = useCallback(() => {
    trackingCallbacks?.onChapterSwipe({
      uniqueChapterId: activeChapter?.id,
      chapterHeadline: activeChapter?.headline,
      chapterTimecode: activeChapter?.timestamp,
      chapterLength: activeChapter?.length,
      buttonLabel: 'Prev|Next',
    })
  }, [
    trackingCallbacks,
    activeChapter?.id,
    activeChapter?.headline,
    activeChapter?.timestamp,
    activeChapter?.length,
  ])

  return chapters?.length ? (
    <BlickTVStoryComponent
      activeItem={activeChapter}
      swiperRef={swiperRef}
      data={chapters}
      showRestartButton={showRestartButton}
      showGoToLiveButton={showGoToLiveButton}
      showPlaceholderButton={hasChaptersPlaceholder(chapters)}
      restartButtonlabel={translate('blickTVStory.restartCurrentChapter')}
      goToLiveButtonlabel={translate('blickTVStory.goToLive')}
      storyTitle={translate('blickTVStory.chapterTitle')}
      onClickRestart={handleOnClickRestart}
      onClickGoToLive={handleOnClickGoToLive}
      onClickItem={handleOnClickChapter}
      onSliderMove={handleOnSliderMoveWithDebounce}
      onNavigationChange={handleOnNavigationChange}
    />
  ) : null
}

export default BlickTVStory
